import {AtLeastOneOf} from 'util/Types';

import {StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {FlowchartConfigMetadata, FlowchartConfigAutosaverPausedConditionType} from './reduceFlowchartConfig';

export enum FlowchartConfigActionType {
  // Updated flowchart config (nodes/edges) or metadata
  UPDATED_FLOWCHART_CONFIG = 'UPDATED_FLOWCHART_CONFIG',
  // For garbage collection when the component is unmounted
  UNMOUNTED_FLOWCHART_CONFIG_EDITOR = 'UNMOUNTED_FLOWCHART_CONFIG_EDITOR',
  DIRTY_STATE_CHANGED = 'DIRTY_STATE_CHANGED',
  READONLY_STATE_CHANGED = 'READONLY_STATE_CHANGED',
  IS_SAVE_IN_FLIGHT_STATE_CHANGED = 'IS_SAVE_IN_FLIGHT_STATE_CHANGED',
  AUTOSAVER_PAUSED_CHANGED = 'AUTOSAVER_PAUSED_CHANGED',
  AUTOSAVER_BLOCKED_CHANGED = 'AUTOSAVER_BLOCKED_CHANGED',
}

type UpdatedFlowchartConfigData = {
  flowchartConfigId: string;
} & AtLeastOneOf<{
  staticFlowchart: StaticTemplateFlowchart;
  flowchartConfigMetadata: Partial<FlowchartConfigMetadata>;
}>;

type FlowchartConfigActionUpdatedFlowchartConfig = {
  type: FlowchartConfigActionType.UPDATED_FLOWCHART_CONFIG;
} & UpdatedFlowchartConfigData;

interface FlowchartConfigActionUnmountedFlowchartConfigEditor {
  type: FlowchartConfigActionType.UNMOUNTED_FLOWCHART_CONFIG_EDITOR;
  flowchartConfigId: string;
}

interface FlowchartConfigBooleanPropertyChanged {
  type:
    | FlowchartConfigActionType.DIRTY_STATE_CHANGED
    | FlowchartConfigActionType.READONLY_STATE_CHANGED
    | FlowchartConfigActionType.IS_SAVE_IN_FLIGHT_STATE_CHANGED
    | FlowchartConfigActionType.AUTOSAVER_BLOCKED_CHANGED;
  flowchartConfigId: string;
  value: boolean;
}

interface FlowchartConfigAutosaverPausedChanged {
  type: FlowchartConfigActionType.AUTOSAVER_PAUSED_CHANGED;
  flowchartConfigId: string;
  condition: FlowchartConfigAutosaverPausedConditionType;
  value: boolean;
}

export type FlowchartConfigAction =
  | FlowchartConfigActionUpdatedFlowchartConfig
  | FlowchartConfigActionUnmountedFlowchartConfigEditor
  | FlowchartConfigBooleanPropertyChanged
  | FlowchartConfigAutosaverPausedChanged;

export function flowchartConfigUpdated(
  params: UpdatedFlowchartConfigData
): FlowchartConfigActionUpdatedFlowchartConfig {
  return {
    ...params,
    type: FlowchartConfigActionType.UPDATED_FLOWCHART_CONFIG,
  };
}

export function flowchartConfigEditorUnmounted(
  flowchartConfigId: string
): FlowchartConfigActionUnmountedFlowchartConfigEditor {
  return {
    type: FlowchartConfigActionType.UNMOUNTED_FLOWCHART_CONFIG_EDITOR,
    flowchartConfigId,
  };
}

export function flowchartConfigDirtyStateChanged(
  flowchartConfigId: string,
  isDirty: boolean
): FlowchartConfigBooleanPropertyChanged {
  return {
    type: FlowchartConfigActionType.DIRTY_STATE_CHANGED,
    flowchartConfigId,
    value: isDirty,
  };
}

export function flowchartConfigReadonlyStateChanged(
  flowchartConfigId: string,
  isReadonly: boolean
): FlowchartConfigBooleanPropertyChanged {
  return {
    type: FlowchartConfigActionType.READONLY_STATE_CHANGED,
    flowchartConfigId,
    value: isReadonly,
  };
}

export function flowchartConfigIsSaveInFlightStateChanged(
  flowchartConfigId: string,
  isSaveInFlight: boolean
): FlowchartConfigBooleanPropertyChanged {
  return {
    type: FlowchartConfigActionType.IS_SAVE_IN_FLIGHT_STATE_CHANGED,
    flowchartConfigId,
    value: isSaveInFlight,
  };
}

export function flowchartConfigAutosaverPausedChanged(
  flowchartConfigId: string,
  condition: FlowchartConfigAutosaverPausedConditionType,
  value: boolean
): FlowchartConfigAutosaverPausedChanged {
  return {
    type: FlowchartConfigActionType.AUTOSAVER_PAUSED_CHANGED,
    flowchartConfigId,
    condition,
    value,
  };
}

export function flowchartConfigAutosaverBlockedChanged(
  flowchartConfigId: string,
  isAutosaverBlocked: boolean
): FlowchartConfigBooleanPropertyChanged {
  return {
    type: FlowchartConfigActionType.AUTOSAVER_BLOCKED_CHANGED,
    flowchartConfigId,
    value: isAutosaverBlocked,
  };
}
